import { useState, type ReactNode } from "react";
import { Toolbar } from "./Toolbar";
import { EM_DASH, humanise } from "../lib/format";

/**
 * Status vocabulary shared by the cross-document table and the company
 * profile. Every status is decided by Tier 2 against the pack's thresholds;
 * nothing here re-scores a value. An earlier version of the cross-document
 * table applied its own 0.5 / 0.9 cut-offs to a score it was reading from the
 * wrong key, so it never showed a disagreement at all.
 */
export const STATUS: Record<string, { label: string; cls: string; rank: number }> = {
    MISMATCH: { label: "Mismatch", cls: "pill-critical", rank: 0 },
    ERROR: { label: "Error", cls: "pill-critical", rank: 1 },
    PARTIAL: { label: "Partial", cls: "pill-warning", rank: 2 },
    UNREADABLE: { label: "Unreadable", cls: "pill-serious", rank: 3 },
    MATCH: { label: "Consistent", cls: "pill-good", rank: 4 },
    SINGLE_SOURCE: { label: "Single source", cls: "pill-info", rank: 5 },
    UNCOMPARED: { label: "Not compared", cls: "", rank: 6 },
    NOT_APPLICABLE: { label: "Not applicable", cls: "", rank: 7 },
    NOT_FOUND: { label: "Not found", cls: "", rank: 8 },
};

export function StatusPill({ status, title }: { status: string; title?: string }) {
    const s = STATUS[status] ?? { label: humanise(status), cls: "" };
    return (
        <span className={`pill ${s.cls}`} title={title}>
            {s.label}
        </span>
    );
}

export const statusRank = (status: string) => STATUS[status]?.rank ?? 99;

function DocChips({ documents }: { documents: string[] }) {
    return (
        <span className="chips" style={{ display: "inline-flex", marginLeft: 6 }}>
            {documents.map((d) => (
                <span key={d} className="chip">{humanise(d)}</span>
            ))}
        </span>
    );
}

function ScalarValues({ attribute }: { attribute: any }) {
    const values: any[] = attribute.values ?? [];
    if (!values.length) return <span className="muted">{EM_DASH}</span>;
    return (
        <>
            {values.map((v, i) => {
                const others = (v.renderings ?? []).filter((r: string) => r !== v.value);
                return (
                    <div key={i} style={{ marginBottom: 4 }}>
                        <strong>{String(v.value)}</strong>
                        {v.canonical && v.canonical !== v.value ? (
                            <span className="muted"> ({v.canonical})</span>
                        ) : null}
                        <DocChips documents={v.documents ?? []} />
                        {others.length ? (
                            <div className="muted" style={{ fontSize: 12 }}>
                                also written {others.map((o: string) => `“${o}”`).join(", ")}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </>
    );
}

function SetItems({ attribute }: { attribute: any }) {
    const items: any[] = attribute.items ?? [];
    if (!items.length) return <span className="muted">{EM_DASH}</span>;
    return (
        <>
            {items.map((item, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                    {String(item.value)}
                    <DocChips documents={item.documents ?? []} />
                    {item.missing_from?.length ? (
                        <span className="pill pill-critical" style={{ marginLeft: 6 }}>
                            not on {item.missing_from.map(humanise).join(", ")}
                        </span>
                    ) : null}
                </div>
            ))}
        </>
    );
}

function Notes({ attribute }: { attribute: any }) {
    const notes: string[] = [];
    for (const u of attribute.unreadable ?? []) {
        notes.push(`Unreadable on ${humanise(u.document_type)}: “${u.value}”`);
    }
    if (attribute.absent_from?.length) {
        notes.push(`Not printed on ${attribute.absent_from.map(humanise).join(", ")}`);
    }
    if (attribute.kind === "date_sequence") {
        for (const p of attribute.pairs ?? []) if (p.detail) notes.push(p.detail);
    }
    if (attribute.basis) notes.push(attribute.basis);
    if (attribute.error) notes.push(`Evaluation error: ${attribute.error}`);
    if (!notes.length) return null;
    return (
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {notes.map((n, i) => <div key={i}>{n}</div>)}
        </div>
    );
}

function scoreTitle(attribute: any): string | undefined {
    const worst = [...(attribute.pairs ?? [])].sort((a, b) => a.score - b.score)[0];
    if (!worst) return attribute.expectation;
    return [
        `${worst.method}${worst.detail ? ` — ${worst.detail}` : ""}`,
        attribute.expectation,
    ].filter(Boolean).join("\n");
}

/**
 * Everything the agents extracted about the company, one row per attribute:
 * each distinct value with the documents that carry it, and whether they
 * agree. Definitions, comparisons and thresholds all live in the Tier 3 pack;
 * this only lays them out.
 */
export function CompanyProfile({ profile }: { profile: any }) {
    const [showEmpty, setShowEmpty] = useState(false);

    if (!profile) {
        return (
            <>
                <Toolbar title="Company Profile" subtitle="Every attribute, as each document reads it" />
                <div className="notice">
                    This verdict was recorded before the company profile existed. Re-evaluate to build it.
                </div>
            </>
        );
    }

    const attributes: any[] = profile.attributes ?? [];
    if (!attributes.length) {
        return (
            <>
                <Toolbar title="Company Profile" subtitle="Every attribute, as each document reads it" />
                <div className="notice">{profile.basis ?? "No profile attributes are declared for this document set."}</div>
            </>
        );
    }

    const hiddenCount = attributes.filter((a) => a.status === "NOT_FOUND").length;
    const visible = showEmpty ? attributes : attributes.filter((a) => a.status !== "NOT_FOUND");

    const groups: { name: string; rows: any[] }[] = [];
    for (const a of visible) {
        const name = a.group ?? "Other";
        let g = groups.find((x) => x.name === name);
        if (!g) groups.push((g = { name, rows: [] }));
        g.rows.push(a);
    }

    const summary: Record<string, number> = profile.summary ?? {};
    const summaryOrder = ["MISMATCH", "PARTIAL", "MATCH", "SINGLE_SOURCE", "UNREADABLE", "ERROR"];
    let sl = 0;

    return (
        <>
            <Toolbar
                title="Company Profile"
                subtitle={`Every attribute the agents extracted, as each document reads it · ${profile.declared_by ?? ""}`}
                right={
                    <span className="pills">
                        {summaryOrder
                            .filter((s) => summary[s])
                            .map((s) => (
                                <span key={s} className={`pill ${STATUS[s]?.cls ?? ""}`}>
                                    {summary[s]} {STATUS[s]?.label.toLowerCase()}
                                </span>
                            ))}
                    </span>
                }
            />
            <div className="card">
                <div className="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th scope="col" className="si">Sl</th>
                                <th scope="col">Attribute</th>
                                <th scope="col" className="cell-prose">Values and the documents that carry them</th>
                                <th scope="col">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groups.map((g) => (
                                <FragmentRows key={g.name} name={g.name}>
                                    {g.rows.map((a) => {
                                        sl += 1;
                                        return (
                                            <tr key={a.code}>
                                                <td className="si">{sl}</td>
                                                <td className="nowrap" title={a.expectation}>{a.label}</td>
                                                <td className="cell-prose">
                                                    {a.kind === "set" ? <SetItems attribute={a} /> : <ScalarValues attribute={a} />}
                                                    <Notes attribute={a} />
                                                </td>
                                                <td className="nowrap">
                                                    <StatusPill status={a.status} title={scoreTitle(a)} />
                                                    {typeof a.score === "number" && a.status !== "MATCH" ? (
                                                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                                                            score {a.score.toFixed(2)}
                                                            {a.thresholds ? ` / needs ${a.thresholds.match.toFixed(2)}` : ""}
                                                        </div>
                                                    ) : null}
                                                    {a.finding ? (
                                                        <div className="mono" style={{ fontSize: 11, marginTop: 2 }}>{a.finding}</div>
                                                    ) : null}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </FragmentRows>
                            ))}
                        </tbody>
                    </table>
                </div>
                {hiddenCount ? (
                    <div className="card-body" style={{ paddingTop: 8 }}>
                        <button className="chip-filter" onClick={() => setShowEmpty((v) => !v)}>
                            {showEmpty ? "Hide" : "Show"} {hiddenCount} attribute{hiddenCount === 1 ? "" : "s"} no document carries
                        </button>
                    </div>
                ) : null}
            </div>
        </>
    );
}

function FragmentRows({ name, children }: { name: string; children: ReactNode }) {
    return (
        <>
            <tr>
                <th scope="colgroup" colSpan={4} className="muted" style={{ textAlign: "left", fontWeight: 600 }}>
                    {name}
                </th>
            </tr>
            {children}
        </>
    );
}
